import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AdBanner, { AdInFeed } from "@/components/feature/AdBanner";
import { useNews } from "@/hooks/useSupabaseData";
import { useAnalytics } from "@/hooks/useAnalytics";
import { usePageSeo } from "@/hooks/usePageSeo";

const itemsPerPage = 6;

const fallbackImage =
  "https://readdy.ai/api/search-image?query=Botafogo%20international%20football%20competition%20professional%20sports%20photography&width=900&height=600&seq=internacional-news&orientation=landscape";

export default function InternacionalPage() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  usePageSeo({
    title: "Internacional | Botafogo na Copa do Mundo e competicoes internacionais",
    description: "Noticias internacionais do Botafogo, Copa do Mundo, SAF, Danilo, Garrincha, competicoes e bastidores do Glorioso.",
    keywords: "Botafogo, Copa do Mundo, Internacional, SAF Botafogo, Danilo, Garrincha, competicoes internacionais, FogaoNet",
  });

  const { news: internacionalNews, loading } = useNews({
    category: "Internacional",
    limit: 200,
  });

  useAnalytics("/internacional");

  const totalPages = Math.ceil(internacionalNews.length / itemsPerPage);
  const paginatedNews = internacionalNews.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="min-h-screen bg-white relative">
      <Navbar />

      <div className="absolute top-0 left-0 right-0 h-48 md:h-64 bg-gradient-to-b from-alvinegro-black to-white z-0" />

      <main className="pt-20 md:pt-24 pb-12 relative z-10">
        <div className="w-full px-4 md:px-8">
          <Breadcrumb items={[{ label: "InÃ­cio", path: "/" }, { label: "Internacional" }]} />

          <div className="mb-8">
            <h1 className="font-serif font-bold text-3xl md:text-4xl text-alvinegro-black mb-4">
              Internacional
            </h1>

            <p className="text-alvinegro-muted text-base">
              Todas as notÃ­cias das competiÃ§Ãµes internacionais envolvendo o Glorioso.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
            <div className="lg:col-span-2">
              {loading ? (
                <div className="space-y-6 animate-pulse">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="flex flex-col md:flex-row gap-4">
                      <div className="w-full md:w-64 aspect-video bg-alvinegro-light rounded-2xl" />

                      <div className="flex-1 space-y-3">
                        <div className="h-5 bg-alvinegro-light rounded w-3/4" />
                        <div className="h-4 bg-alvinegro-light rounded w-full" />
                        <div className="h-4 bg-alvinegro-light rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : internacionalNews.length === 0 ? (
                <div className="bg-alvinegro-light rounded-2xl p-6 text-sm text-alvinegro-muted">
                  Nenhuma notÃ­cia cadastrada na categoria Internacional.
                </div>
              ) : (
                <>
                  <div className="space-y-6">
                    {paginatedNews.map((news, index) => (
                      <div key={news.id}>
                        <article
                          className="group cursor-pointer border-b border-alvinegro-border pb-6 last:border-b-0"
                          onClick={() => navigate(`/noticias/${news.slug}`)}
                        >
                          <div className="flex flex-col md:flex-row gap-5 md:gap-6">
                            <div className="w-full md:w-[58%] aspect-[16/9] rounded-2xl overflow-hidden bg-alvinegro-light shrink-0">
                              <img
                                src={news.image_url || fallbackImage}
                                alt={news.title}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                            </div>

                            <div className="flex-1 min-w-0">
                              <span className="inline-flex bg-alvinegro-black text-white text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
                                {news.category}
                              </span>

                              <h2 className="font-serif font-bold text-xl md:text-2xl text-alvinegro-black leading-tight group-hover:text-alvinegro-gray transition-colors">
                                {news.title}
                              </h2>

                              <p className="text-alvinegro-muted text-sm md:text-base mt-2 line-clamp-2">
                                {news.subtitle}
                              </p>

                              <div className="flex items-center gap-2 mt-3 text-xs text-alvinegro-muted">
                                <span>
                                  {news.published_at
                                    ? new Date(news.published_at).toLocaleDateString("pt-BR")
                                    : ""}
                                </span>
                              </div>
                            </div>
                          </div>
                        </article>

                        {index === 2 && paginatedNews.length > 3 && (
                          <div className="my-6 md:my-8">
                            <AdInFeed />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-8">
                      <button
                        onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                        disabled={currentPage === 1}
                        className="w-10 h-10 rounded-full flex items-center justify-center bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border disabled:opacity-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-left-s-line" />
                      </button>

                      {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors cursor-pointer ${
                            currentPage === page
                              ? "bg-alvinegro-black text-white"
                              : "bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border"
                          }`}
                        >
                          {page}
                        </button>
                      ))}

                      <button
                        onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                        disabled={currentPage === totalPages}
                        className="w-10 h-10 rounded-full flex items-center justify-center bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border disabled:opacity-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-right-s-line" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="space-y-6">
              <AdBanner variant="medium-rectangle" label="Publicidade" />
              <AdBanner variant="medium-rectangle" label="Publicidade" />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
